<script setup>
import {computed, defineProps} from "vue";

const props = defineProps({
	content: Object
});

const wrapperClass = (() => {
	switch (props.content.content_type_key) {
		case 'YouTube':
		case 'Vimeo':
			return 'md:w-3/5 h-[70vh] w-full flex justify-center items-center overflow-hidden';
		case 'GoogleSlides':
		case 'GoogleDocs':
		case 'GoogleSheets':
			return 'md:w-3/5 h-[90vh] w-full flex justify-center items-center overflow-hidden';
		case 'SoundCloud':
		case 'Spotify':
		case 'SpotifyEpisode':
		case 'SpotifyAlbum':
		case 'SpotifyPlaylist':
			return 'flex flex-col items-center space-y-6 bg-gray-200 py-10 px-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300';
		case 'Prezi':
			return 'md:w-4/5 h-[70vh] w-full flex justify-center items-center overflow-hidden';
		case 'GoogleForms':
		case 'SurveyMonkey':
		case 'Quizlet':
			return 'md:w-3/5 h-[70vh] w-full flex justify-center items-center overflow-hidden';
		case 'Website':
			return 'flex justify-center items-center overflow-hidden website-preview';
		default:
			return 'md:w-3/5 h-[70vh] w-full flex justify-center items-center overflow-hidden';
	}
});

const embedSettings = computed(() => {
	switch (props.content.content_type_key) {
		
		//// START VIDEO SECTION ////
		case 'YouTube':
			return {
				src: `https://www.youtube.com/embed/${props.content.value}`,
				class: 'inset-0 w-full h-full border-none',
			};
		case 'Vimeo':
			return {
				src: `https://player.vimeo.com/video/${props.content.value}?${props.content.params}`,
				class: 'inset-0 w-full h-full border-none',
			};
		
		//// START AUDIO SECTION ////
		case 'SoundCloud':
			return {
				scrolling: 'no',
				frameborder: 'no',
				class: 'md:w-[700px] md:h-[700px]',
				allow: 'autoplay',
				src: `https://w.soundcloud.com/player/?url=https%3A//api.soundcloud.com/tracks/${props.content.value}&color=%23ff5500&auto_play=false&hide_related=true&show_comments=false&show_user=true&show_reposts=false&show_teaser=false&visual=true`,
			};
		case 'Spotify':
			return {
				style: 'border-radius:12px',
				src: `https://open.spotify.com/embed/track/${props.content.value}?utm_source=generator`,
				width: '100%',
				height: '352',
				frameborder: '0',
				allowfullscreen: '',
				allow: 'autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture',
				loading: 'lazy',
			};
		case 'SpotifyEpisode':
			return {
				style: 'border-radius:12px',
				src: `https://open.spotify.com/embed/episode/${props.content.value}?utm_source=generator`,
				width: '100%',
				height: '352',
				frameborder: '0',
				allowfullscreen: '',
				allow: 'autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture',
				loading: 'lazy',
			};
		case 'SpotifyAlbum':
			return {
				style: 'border-radius:12px',
				src: `https://open.spotify.com/embed/album/${props.content.value}?utm_source=generator`,
				width: '100%',
				height: '352',
				frameborder: '0',
				allowfullscreen: '',
				allow: 'autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture',
				loading: 'lazy',
			};
		case 'SpotifyPlaylist':
			return {
				style: 'border-radius:12px',
				src: `https://open.spotify.com/embed/playlist/${props.content.value}?utm_source=generator`,
				width: '100%',
				height: '352',
				frameborder: '0',
				allowfullscreen: '',
				allow: 'autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture',
				loading: 'lazy',
			};
		
		//// START DOCUMENTS SECTION ////
		case 'Prezi':
			return {
				src: `https://prezi.com/p/embed/${props.content.value}`,
				class: 'inset-0 w-full h-full border-none',
			};
		case 'GoogleDocs':
			return {
				src: `https://docs.google.com/document/d/e/${props.content.value}/pub?embedded=true`,
				class: 'inset-0 w-full h-full border-none',
			};
		case 'GoogleSheets':
			return {
				src: `https://docs.google.com/spreadsheets/d/e/${props.content.value}/pubhtml?widget=true&amp;headers=false`,
				class: 'inset-0 w-full h-full border-none',
			};
		case 'GoogleSlides':
			return {
				src: `https://docs.google.com/presentation/d/e/${props.content.value}/embed`,
				class: 'inset-0 w-full h-full border-none',
				allowfullscreen: 'true',
				mozallowfullscreen: 'true',
				webkitallowfullscreen: 'true',
			};
		case 'GoogleDocs2024':
			return {
				src: `https://docs.google.com/document/d/${props.content.value}/pub?embedded=true`,
				class: 'inset-0 w-full h-full border-none',
			};
		case 'GoogleSheets2024':
			return {
				src: `https://docs.google.com/spreadsheets/d/${props.content.value}/pubhtml?widget=true&amp;headers=false`,
				class: 'inset-0 w-full h-full border-none',
			};
		case 'GoogleSlides2024':
			return {
				src: `https://docs.google.com/presentation/d/${props.content.value}/embed`,
				class: 'inset-0 w-full h-full border-none',
				allowfullscreen: 'true',
				mozallowfullscreen: 'true',
				webkitallowfullscreen: 'true',
			};
		case 'Canva':
			return {
				src: `https://www.canva.com/design/${props.content.value}/view?embed`,
				class: 'inset-0 w-full h-full border-none',
				allowfullscreen: 'true',
				mozallowfullscreen: 'true',
				webkitallowfullscreen: 'true',
				loading: 'lazy'
			};
		
		//// START QUIZ SECTION ////
		case 'SurveyMonkey':
			return {
				src: `https://www.surveymonkey.com/r/${props.content.value}`,
				class: 'inset-0 w-full h-full border-none',
			};
		case 'Quizlet':
			return {
				src: `https://quizlet.com/za/${props.content.value}/test-flash-cards/?i=571lqc&x=1jqt`,
				class: 'inset-0 w-full h-full border-none',
			};
		case 'GoogleForms':
			return {
				src: `https://docs.google.com/forms/d/e/${props.content.value}/viewform?embedded=true`,
				class: 'inset-0 w-full h-full border-none',
			};
		
		//// START WEB ////
		case 'Website':
			return {
				src: `${props.content.value}`,
				class: 'inset-0 w-full h-full border-none',
			};
		default:
			return '';
	}
});
</script>

<template>
	<div :class="wrapperClass()">
		<iframe v-bind="embedSettings"/>
	</div>
</template>